import { inject, Injectable, Injector } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { isArray } from 'lodash';

// Types
import { FailedCall, HasAcceptedTermsAndConditions } from '../../../shared/types/manageCalls';
import { TrackingActionsParams } from '../../../shared/types/params';

// Services
import { LoggerService } from '../../services/logger/logger.service';
import { OfflineService } from '../../services/offline/offline.service';
import { WebServiceCaller } from '../../services/webservice-caller/webservice-caller.service';

@Injectable()
export class AppFunctionalitiesService {
  private readonly wsCaller = inject(WebServiceCaller);
  private readonly injector = inject(Injector);
  private readonly logger = inject(LoggerService);

  public trackAction(
    trackInfo: TrackingActionsParams | Array<TrackingActionsParams>,
    isFromCheckOfflineApis: boolean = false,
  ): Observable<void | FailedCall> {
    type Response = void | FailedCall;

    if (isFromCheckOfflineApis) {
      return this.wsCaller.postCall<Response>('trackingActions', trackInfo);
    } else {
      const payload = (isArray(trackInfo) ? trackInfo : [trackInfo]) as Array<TrackingActionsParams>;

      if ((trackInfo as TrackingActionsParams).timestampOffline) {
        return this.wsCaller.postCall<Response>('trackingActions', payload);
      } else {
        return this.wsCaller.postCall<Response>('trackingActions', payload).pipe(
          catchError((error: FailedCall) => {
            this.logger.consoleError('AppFunctionalitiesService', 'trackAction', { error });
            const offline = this.injector.get(OfflineService);

            if (error === null || error?.code === 'CORS_ERROR') {
              const params: TrackingActionsParams = { ...trackInfo, timestampOffline: Date.now() };
              offline.addApiToCallWhenOnline('trackAction', params);
            }

            return throwError(() => error);
          }),
        );
      }
    }
  }

  public acceptTermsAndConditions(): Observable<void | FailedCall> {
    return this.wsCaller.putCall('acceptTermsAndConditions');
  }

  public hasAcceptedTermsAndConditions(): Observable<HasAcceptedTermsAndConditions | FailedCall> {
    return this.wsCaller.postCall('hasAcceptedTermsAndConditions');
  }
}
